export interface CreateDocumentData {
  property_id: number;
  original_filename: string;
  document_type: 'plan' | 'title_deed' | 'invoice' | 'permit' | 'contract' | 'photo' | 'other';
  document_category?: string;
  description?: string;
  is_confidential?: boolean;
}

export interface UpdateDocumentData extends Partial<CreateDocumentData> {
  status?: 'active' | 'archived' | 'deleted';
}

export interface PropertyDocument {
  id: number;
  property_id: number;
  agency_id: number;
  uploaded_by_user_id: number;
  original_filename: string;
  stored_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  file_extension: string;
  document_type: 'plan' | 'title_deed' | 'invoice' | 'permit' | 'contract' | 'photo' | 'other';
  document_category?: string;
  description?: string;
  is_confidential: boolean;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  uploaded_by_name?: string;
}

class DocumentAPI {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/documents`;

  async getByProperty(propertyId: number) {
    const response = await fetch(`${this.baseUrl}/property/${propertyId}`);
    if (!response.ok) throw new Error('Failed to fetch documents');
    return response.json();
  }

  async upload(propertyId: number, file: File, metadata: Omit<CreateDocumentData, 'property_id' | 'original_filename'>) {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('property_id', propertyId.toString());
    formData.append('document_type', metadata.document_type);

    if (metadata.document_category) {
      formData.append('document_category', metadata.document_category);
    }
    if (metadata.description) {
      formData.append('description', metadata.description);
    }
    if (metadata.is_confidential) {
      formData.append('is_confidential', metadata.is_confidential.toString());
    }

    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload document');
    }

    return response.json();
  }

  async update(id: number, data: UpdateDocumentData) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update document');
    }
    return response.json();
  }

  async delete(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete document');
    }
    return response.json();
  }

  async download(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}/download`);
    if (!response.ok) throw new Error('Failed to download document');
    return response.blob();
  }

  async archive(id: number) {
    return this.update(id, { status: 'archived' });
  }
}

export const documentAPI = new DocumentAPI();
